import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Breadcrumbs from '../../components/BreadCrumbs';
import { MarketingTileItem } from '../../components/MarketingTiles';
import { EventType, useAnalytics } from '../../ui/Analytics';
import MarketingTileModal from '../../components/MarketingTileModal';
import {
  RootState,
  renderDescriptionParagraphs,
  accountProtectionRequest,
  contactInfoRequest,
} from '@rb-serve-app/core';
import { useNavigate } from 'react-router-dom';
import CommanModal from '../../components/CommanModal';
import { logBrazeCustomEvent } from '../../config/braze';

const DEFAULT_MORE_OFFERS_URL = {
  en: 'https://www.rogersbank.com/en/special-offers/',
  fr: 'https://www.rogersbank.com/fr/special-offers/',
};

const ChevronIcon: React.FC<{ color?: string }> = ({ color = 'white' }) => (
  <svg
    className="ml-1.5 w-4 h-4 md:w-5 md:h-5"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 12L10 8L6 4"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const mapBannerToTile = (banner: any): MarketingTileItem => ({
  id: banner.id.toString(),
  title: banner.title || '',
  ctaText: banner.ctaText || 'Learn More',
  popupCtaText: banner.content_cta || banner.ctaText || 'Learn More',
  imageUrl: banner.imgUrl || '',
  description: banner.description || '',
  link: banner.link || '',
  bannerData: banner,
  disclaimerLabel: banner.disclaimer_label,
  disclaimerContent: banner.disclaimer_content,
  bullets: banner.bullets || [],
  logos: banner.logos || [],
  exclusionList: banner.exclusionList || [],
});

const getTilesForProduct = (
  contentfulData: any,
  productName?: string,
  provinceCode?: string
): MarketingTileItem[] => {
  if (!contentfulData || !productName) {
    return [];
  }

  const promotionalOffers =
    contentfulData?.entries?.rb_marketing_tiles?.makretingPromotionalOffers;

  if (!Array.isArray(promotionalOffers)) {
    return [];
  }

  const productOffer = promotionalOffers.find(
    (offer: { productName?: string }) => offer?.productName === productName
  );

  const banners = productOffer?.data?.promotional_banners;
  if (!Array.isArray(banners)) {
    return [];
  }
  return banners
    .map(mapBannerToTile)
    .filter(
      (tile) =>
        !provinceCode ||
        !tile.exclusionList?.length ||
        !tile.exclusionList.includes(provinceCode)
    );
};

type OfferCardProps = {
  offer: any;
  assets: Record<string, string>;
  onTileClick: (tile: MarketingTileItem) => void;
};

const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  assets,
  onTileClick,
}) => (
  <li className="flex flex-col overflow-hidden border border-gray-200 bg-white md:flex-row lg:max-h-[320px] xl:max-h-[320px] md:max-h-[320px] h-full p-4">
    <div className="md:w-[30%] w-full h-auto">
      {assets && offer?.imageUrl && (
        <img
          src={assets[offer.imageUrl]}
          alt={
            offer.description
              ? `${offer.title} - ${offer.description}`
              : offer.title
          }
          className="w-full md:h-full h-[289px] xl:h-full lg:h-full object-contain"
        />
      )}
    </div>
    <div className="flex flex-1 flex-col justify-between pt-2 md:p-6 md:w-[70%] w-full">
      <div>
        <h3 className="text-[22px] font-semibold leading-snug text-gray-800">
          {offer.title}
        </h3>
        {offer.description && (
          <p className="mt-3 text-[15px] leading-6 text-gray-600 max-h-full md:max-h-[97px] h-full overflow-y-auto pr-0 [scrollbar-width:thin] [scrollbar-color:#cbd5e1_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
            {renderDescriptionParagraphs(
              offer.description,
              'text-[15px] md:text-[15px] text-gray-600 max-w-4xl leading-6'
            )}
          </p>
        )}
        {offer.bullets && offer.bullets.length > 0 && (
          <ul className="mt-3 list-disc space-y-2 pl-5 text-[15px] leading-6 text-gray-600">
            {offer.bullets.map((bullet: any, bulletIndex: number) => (
              <li key={`${offer.id}-bullet-${bulletIndex}`}>{bullet}</li>
            ))}
          </ul>
        )}
        {offer.logos && offer.logos.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm font-semibold text-gray-500">
            {offer.logos.map((logo: any, logoIndex: number) => (
              <span
                key={`${offer.id}-logo-${logoIndex}`}
                className="rounded border border-gray-300 px-3 py-1 text-xs uppercase tracking-wide"
              >
                {logo}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="mt-4">
        <button
          className="inline-flex items-center justify-center gap-2 bg-primary-red px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-600 md:w-[167px] lg:w-[167px] xl:w-[167px] h-full w-full h-[48px]"
          onClick={() => onTileClick(offer)}
          aria-haspopup="dialog"
        >
          {offer.ctaText}
          <span aria-hidden="true">
            <ChevronIcon color="white" />
          </span>
        </button>
      </div>
    </div>
  </li>
);

const OffersMarketingTiles = () => {
  const [marketingTiles, setMarketingTiles] = useState<MarketingTileItem[]>([]);
  const [selectedTile, setSelectedTile] = useState<MarketingTileItem | null>(
    null
  );
  const [showTileModal, setShowTileModal] = useState(false);
  const [showNotEligibleModal, setShowNotEligibleModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { contentfulData } = useSelector((state: any) => state.contentful);
  const {
    accountId,
    customerId,
    accountDetails = {},
  } = useSelector((state: RootState) => state.user);
  const { trackEvent } = useAnalytics();
  const { entries = {}, assets = {} } = contentfulData || {};
  const { insurancedetailSuccess, insurancedetailError } = useSelector(
    (state: RootState) => state.accountProtection
  );
  const { contactDetails } = useSelector((state: any) => state.contactInfo);
  const { provinceCode } = useSelector((state: any) => state.userLocation);

  const dispatch = useDispatch();
  useEffect(() => {
    if (accountId && customerId) {
      if (insurancedetailSuccess === null) {
        dispatch(accountProtectionRequest({ accountId, customerId }));
      }
      if (provinceCode == null && !contactDetails) {
        dispatch(
          contactInfoRequest({
            accountId,
            primaryCustomerId: customerId,
            customerId,
          })
        );
      }
    }
  }, [accountId, customerId, dispatch]);
  const navigate = useNavigate();

  useEffect(() => {
    logBrazeCustomEvent('viewed_offers');
  }, []);

  const getMoreOffersUrl = () => {
    const lang = (localStorage.getItem('language') || 'en-US').toLowerCase();
    const isFrench = lang.startsWith('fr');
    const moreOffersLabel =
      entries?.rb_offers?.rbViewallOffers?.offers?.moreOffers?.btnText ||
      'more offers';
    const moreOffersUrl =
      entries?.rb_offers?.rbViewallOffers?.offers?.moreOffers?.specialOffersURL;

    trackEvent(
      EventType.CLICK_EVENT,
      'offers',
      '',
      `offer|${moreOffersLabel}|clicked`
    );

    if (moreOffersUrl) {
      return moreOffersUrl;
    }

    return isFrench ? DEFAULT_MORE_OFFERS_URL.fr : DEFAULT_MORE_OFFERS_URL.en;
  };

  const handleMoreOffersClick = () => {
    const targetUrl = getMoreOffersUrl();
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleLearnMore = (tile: MarketingTileItem) => {
    console.log('Tile clicked:', tile);
    if (tile) {
      console.log('Tile clicked:', tile);
      setSelectedTile(tile);
      trackEvent(
        EventType.CLICK_EVENT,
        'dashboard',
        '',
        `offers|${tile.title}`
      );
      if (tile?.bannerData?.type === 'RAP') {
        if (insurancedetailError) {
          setShowErrorModal(true);
          return;
        }
        if (insurancedetailSuccess?.insuranceEligibilityFlag === false) {
          setShowNotEligibleModal(true);
          return;
        }
        navigate('/account-protection?redirectedFrom=offers');
      } else if (tile.link) {
        window.open(tile.link, '_blank');
      }
    }
  };

  useEffect(() => {
    if(provinceCode){
    const tiles = getTilesForProduct(
      contentfulData,
      accountDetails?.productName,
      provinceCode
    );
    setMarketingTiles(tiles);
  }
  }, [contentfulData, accountDetails?.productName, provinceCode]);

  const breadcrumbs = [
    {
      name: entries?.rb_navigation?.homeLogoTitle || 'Home',
      href: '/home',
    },
    {
      name: entries?.rb_offers?.rbViewallOffers?.offers?.headings?.pageTitle,
      href: '/offers',
    },
  ];

  return (
    <>
      <div className="bg-bg-body py-4 md:px-8 px-4">
        <Breadcrumbs items={breadcrumbs} />

        {/* White Container */}
        <div className="w-full m-[22px] mx-auto rounded-[15px] bg-white min-h-[70vh] my-0">
          <div className="bg-white md:p-6 p-4 rounded-lg">
            <h1 className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] pb-7">
              {entries?.rb_offers?.rbViewallOffers?.offers?.headings?.pageTitle}
            </h1>

            {marketingTiles.length > 0 && (
              <ul className="space-y-8" aria-live="polite">
                {marketingTiles.map((offer) => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    assets={assets}
                    onTileClick={() => handleLearnMore(offer)}
                  />
                ))}
              </ul>
            )}

            {marketingTiles.length > 0 && (
              <div className="inline-flex  item-center justify-center mt-10 w-full">
                <button
                  onClick={handleMoreOffersClick}
                  className="inline-flex items-center justify-center  gap-2 bg-white px-5 py-3 text-sm font-semibold text-primary-red transition hover:bg-rose-100  xl:w-[287px] lg:w-[287px] md:w-[287px] w-full h-[48px] border-primary-red border"
                >
                  {entries?.rb_offers?.ctaTexts?.moreOffersCta || 'More Offers'}
                  <span aria-hidden="true">
                    <ChevronIcon color="#DA291C" />
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Marketing Tile Modal */}
        <MarketingTileModal
          isOpen={showTileModal}
          onClose={() => setShowTileModal(false)}
          tile={selectedTile}
          assets={assets}
        />
      </div>
      <CommanModal
        isOpen={showNotEligibleModal || showErrorModal}
        jsxContent={
          <>
            <div
              className="mt-[22px] text-lg font-semibold text-center"
              aria-label={
                showErrorModal
                  ? entries?.rb_offers?.rbViewallOffers?.offers
                      ?.temporarilyUnavailableMsg?.title || 'Error'
                  : entries?.rb_offers?.rbViewallOffers?.offers?.notEligibleMsg
                      ?.title || 'Not Eligible'
              }
            >
              {showErrorModal
                ? entries?.rb_offers?.rbViewallOffers?.offers
                    ?.temporarilyUnavailableMsg?.title || 'Error'
                : entries?.rb_offers?.rbViewallOffers?.offers?.notEligibleMsg
                    ?.title || 'Not Eligible'}
            </div>
            <div
              className="mt-[16px] text-center"
              aria-label={
                showErrorModal
                  ? entries?.rb_offers?.rbViewallOffers?.offers
                      ?.temporarilyUnavailableMsg?.description || 'Error'
                  : entries?.rb_offers?.rbViewallOffers?.offers?.notEligibleMsg
                      ?.description ||
                    'You may not be eligible to enroll in Rogers Account Protection. Please contact us at 1-855-775-2265.'
              }
            >
              {showErrorModal
                ? entries?.rb_offers?.rbViewallOffers?.offers
                    ?.temporarilyUnavailableMsg?.description || 'Error'
                : entries?.rb_offers?.rbViewallOffers?.offers?.notEligibleMsg
                    ?.description ||
                  'You may not be eligible to enroll in Rogers Account Protection. Please contact us at 1-855-775-2265.'}
            </div>
          </>
        }
        onClose={() => {
          setShowNotEligibleModal(false);
          setShowErrorModal(false);
        }}
        buttonText=""
        extraButtonText={
          showErrorModal
            ? entries?.rb_offers?.rbViewallOffers?.offers
                ?.temporarilyUnavailableMsg?.ctaText || 'Ok'
            : entries?.rb_offers?.rbViewallOffers?.offers?.notEligibleMsg
                ?.ctaText || 'Ok'
        }
        onExtraButtonClick={() => {
          setShowNotEligibleModal(false);
          setShowErrorModal(false);
        }}
        cardStatus={true}
        closeIcon={false}
      />
    </>
  );
};

export default OffersMarketingTiles;
